import { i18n, _t, isEn } from "app/i18n/i18n";
import ErrorBoundary from "lib/ErrorBoundary";
import useIsMobile from "lib/hooks/useIsMobile";
import { useQueryParams } from "lib/hooks/useQueryParams";
import { useGetPreferences } from "api/preferences/useGetPreferences";
import { useGetMenuBarData } from "pages/redesign/menuFilterBar/api/getMenuBarData";
import { useGetSectorData } from "pages/redesign/menuFilterBar/api/getSectorData";
import { Checkboxes } from "lib/Checkboxes";
import { getAssetClass } from "pages/redesign/publications/api/searchRequest/urlHelpers";
import React, { useState } from "react";
import { Button, Row } from "react-bootstrap";
import { PUB_LANG } from "redux/slices/api/initialisePreferences";
import { LoadingSpinner } from "lib/Spinner";
import { getSearchOptions } from "./api/getSearchOptions";
import {
  analystAutoComplete,
  countryAutoComplete,
  FilterAutoComplete,
  periodicalAutoComplete,
  regionAutoComplete,
  companyAutoComplete,
  currencyAutoComplete,
  tradeIdeasAutoComplete,
} from "./autocomplete/filterAutoComplete";
import { CollapsibleFilter } from "../CollapsibleFilter/CollapsibleFilter";
import { SearchDatePicker } from "./datepicker/SearchDatePicker";
import "./filterPanel.css";
import EventsCarousel from "./EventsCarousel/EventsCarousel";
import ThemeInFocus from "components/ThemeInFocus/ThemeInFocus";

export function FilterPanel({ aggregations, themeInFocusData }) {
  const lang = i18n.getLanguage();
  const qs = useQueryParams();
  const sectorData = useGetSectorData();
  const assetClass = getAssetClass(qs);
  const pubLangPref = useGetPreferences(PUB_LANG);
  const [showCustomDates, setShowCustomDates] = useState(false);
  const isMobile = useIsMobile();
  const menuFormData = useGetMenuBarData();

  if (!aggregations || !Object.keys(aggregations).length) return <LoadingSpinner />;

  const formData = getSearchOptions(lang, qs, pubLangPref, aggregations, assetClass, sectorData);

  return (
    <section className="pl-3 mr-2 text-secondary accent-primary">
      {isMobile && (
        <CollapsibleFilter label={_t("Region")} icon="fa fa-globe" isHighlighted={isRegionSelected(menuFormData)}>
          <Checkboxes name="regions" formData={menuFormData} />
        </CollapsibleFilter>
      )}
      {isMobile && (
        <CollapsibleFilter
          label={_t("Periodical")}
          icon="fas fa-newspaper"
          isHighlighted={isPeriodicalSelected(menuFormData)}
          className="max-height-333 overflow-y-auto"
        >
          <FilterAutoComplete {...periodicalAutoComplete} />
          <Checkboxes name="periodicals" formData={menuFormData} />
        </CollapsibleFilter>
      )}
      {isMobile && (
        <CollapsibleFilter label={_t("Category")} icon="fa fa-tags" isHighlighted={isCategorySelected(menuFormData)}>
          <Checkboxes name="txSubCategories" formData={menuFormData} />
        </CollapsibleFilter>
      )}
      {!!formData.analysts.isVisible && (
        <CollapsibleFilter label={_t("Analyst")} icon="fa fa-user" isHighlighted={formData.analysts.values.length}>
          <FilterAutoComplete {...analystAutoComplete} />
          <Checkboxes name="analysts" formData={formData} />
        </CollapsibleFilter>
      )}

      {!!formData.regions.isVisible && (
        <CollapsibleFilter
          label={_t("Region")}
          icon="fa fa-globe-americas"
          isHighlighted={formData.regions.values.length}
        >
          <FilterAutoComplete {...regionAutoComplete} />
          <Checkboxes name="regions" formData={formData} />
        </CollapsibleFilter>
      )}
      {!!formData.countries.isVisible && (
        <CollapsibleFilter label={_t("Country")} icon="fa fa-flag" isHighlighted={formData.countries.values.length}>
          <FilterAutoComplete {...countryAutoComplete} />
          <Checkboxes name="countries" formData={formData} />
        </CollapsibleFilter>
      )}
      {!!formData.currencies.isVisible && (
        <CollapsibleFilter label={_t("Currency")} icon="fas fa-coins" isHighlighted={formData.currencies.values.length}>
          <FilterAutoComplete {...currencyAutoComplete} />
          <Checkboxes name="currencies" formData={formData} />
        </CollapsibleFilter>
      )}
      {!!formData.tradeIdeas.isVisible && (
        <CollapsibleFilter label={_t("Trade ideas (FX/Rates)")} icon="fas fa-chart-line" isHighlighted={formData.tradeIdeas.values.length}>
            <FilterAutoComplete {...tradeIdeasAutoComplete} />
            <Checkboxes name="tradeIdeas" formData={formData} />
        </CollapsibleFilter>
      )}

      {!!formData.themes.isVisible && (
        <CollapsibleFilter label={_t("Theme")} icon="far fa-lightbulb" isHighlighted={formData.themes.values.length}>
          <Checkboxes name="themes" formData={formData} />
        </CollapsibleFilter>
      )}

      {!!formData.companies.isVisible && (
        <CollapsibleFilter label={_t("Company")} icon="fa fa-hotel" isHighlighted={formData.companies.values.length}>
          <FilterAutoComplete {...companyAutoComplete} />
          <Checkboxes name="companies" formData={formData} />
        </CollapsibleFilter>
      )}
      {!!formData.sectors.isVisible && (
        <CollapsibleFilter label={_t("Sector")} icon="fa fa-industry" isHighlighted={formData.sectors.values.length}>
          <Checkboxes name="sectors" formData={formData} />
        </CollapsibleFilter>
      )}
      {!!formData.industryGroups.isVisible && (
        <CollapsibleFilter
          label={_t("Sub-Sector")}
          icon="fas fa-hard-hat"
          isHighlighted={formData.industryGroups.values.length}
        >
          <Checkboxes name="industryGroups" formData={formData} />
        </CollapsibleFilter>
      )}
      {!!formData.ratings.isVisible && (
        <CollapsibleFilter
          label={_t("Price/Rating")}
          icon="fas fa-bullseye"
          isHighlighted={formData.ratings.values.length}
          className="whitespace-pre "
        >
          <Checkboxes name="ratings" formData={formData} />
        </CollapsibleFilter>
      )}
      {!!formData.contentType.isVisible && (
        <CollapsibleFilter
          label={_t("Format")}
          icon="fas fa-photo-video"
          isHighlighted={formData.contentType.values.length}
        >
          <Checkboxes name="contentType" formData={formData} />
        </CollapsibleFilter>
      )}
      <CollapsibleFilter
        label={_t("Date_2")}
        icon="fas fa-calendar-alt"
        isHighlighted={!formData.dates.values.includes("All Time")}
      >
        <Row className="pl-4">
          <Checkboxes name="dates" formData={formData} className="d-block col-6" />
          {ShowDatesButton(setShowCustomDates)}
        </Row>
        {showCustomDates ? CustomDates(formData) : null}
      </CollapsibleFilter>
      <CollapsibleFilter label={_t("Language")} icon="fa fa-language">
        <Checkboxes name="languages" formData={formData} />
      </CollapsibleFilter>
      <ThemeInFocus themes={themeInFocusData} />
      <EventsSection />
    </section>
  );
}

function ShowDatesButton(setShowCustomDates) {
  return (
    <Button variant="link col-6 my-0 p-0 ml-0 text-secondary" onClick={() => setShowCustomDates((bool) => !bool)}>
      {_t("Custom range")}
    </Button>
  );
}

export function EventsSection() {
  return (
    isEn() && (
      <div className="events-section">
        <i className="pr-2 fa-fw fa fa-calendar" />
        <label className="events-label">{_t("Upcoming Events")}</label>
        <EventsCarousel fade />
      </div>
    )
  );
}

function CustomDates(formData) {
  return (
    <ErrorBoundary>
      <SearchDatePicker label="From date" filter={{ Date: formData.dates.date }} />
      <SearchDatePicker label="To date" filter={{ DateTo: formData.dates.dateTo }} />
    </ErrorBoundary>
  );
}

const isRegionSelected = (formData) => !formData.regions.values.includes("All Regions");
const isPeriodicalSelected = (formData) => !formData.periodicals.values.includes(_t("All Periodicals"));
const isCategorySelected = (formData) => !formData.txSubCategories.values.includes(_t("All Categories"));
